import * as RadixThemes from '@radix-ui/themes'

export { default as AiSuggestTextArea } from './AiSuggestTextArea'
export { default as DataTable } from './DataTable'
export { default as DiscoCallout } from './DiscoCallout'
export { default as DiscoSpinner } from './DiscoSpinner'
export { default as DiscoStackLoader } from './DiscoStackLoader'
export { default as PasswordTextField } from './PasswordTextField'
export { default as ProgressBarLoader } from './ProgressBarLoader'
export type {
  SearchableDropdownGroup,
  SearchableDropdownOption,
  SearchableDropdownProps,
} from './SearchableDropdown'
export { SearchableDropdown } from './SearchableDropdown'
export type { CreateNewActionProps } from './SearchableDropdown/CreateNewAction'
export { CreateNewAction } from './SearchableDropdown/CreateNewAction'
export type { OptionWithEditIconProps } from './SearchableDropdown/OptionWithEditIcon'
export { OptionWithEditIcon } from './SearchableDropdown/OptionWithEditIcon'
export { default as SearchTextField } from './SearchTextField'
export { default as SearchTypeSelector } from './SearchTypeSelector'
export type { SplitButtonDropdownItem, SplitButtonProps } from './SplitButton'
export { default as SplitButton } from './SplitButton'
export { default as TextArea } from './TextArea'

/**
 * Accessible icon component with aria-label support.
 * @see https://www.radix-ui.com/themes/docs/components/accessible-icon
 */
export const AccessibleIcon = RadixThemes.AccessibleIcon
export const Avatar = RadixThemes.Avatar

/**
 * Badge component for displaying status, labels, or categories.
 * @see https://www.radix-ui.com/themes/docs/components/badge
 */
export const Badge = RadixThemes.Badge

/**
 * Layout primitive for containing and positioning content.
 * @see https://www.radix-ui.com/themes/docs/components/box
 */
export const Box = RadixThemes.Box

/**
 * Interactive button component with various styles and states.
 * @see https://www.radix-ui.com/themes/docs/components/button
 */
export const Button = RadixThemes.Button

/**
 * Callout component for displaying important information or alerts.
 * @see https://www.radix-ui.com/themes/docs/components/callout
 */
export const Callout = RadixThemes.Callout

/**
 * Card component for grouping related content in a contained layout.
 * @see https://www.radix-ui.com/themes/docs/components/card
 */
export const Card = RadixThemes.Card

/**
 * Checkbox input component with label and form integration.
 * @see https://www.radix-ui.com/themes/docs/components/checkbox
 */
export const Checkbox = RadixThemes.Checkbox

/**
 * Container component for centering and constraining content width.
 * @see https://www.radix-ui.com/themes/docs/components/container
 */
export const Container = RadixThemes.Container

/**
 * Modal dialog component for displaying overlaid content.
 * @see https://www.radix-ui.com/themes/docs/components/dialog
 */
export const Dialog = RadixThemes.Dialog

/**
 * Dropdown menu component for contextual actions and navigation.
 * @see https://www.radix-ui.com/themes/docs/components/dropdown-menu
 */
export const DropdownMenu = RadixThemes.DropdownMenu

/**
 * Flexbox layout component for arranging items with flexible spacing.
 * @see https://www.radix-ui.com/themes/docs/components/flex
 */
export const Flex = RadixThemes.Flex

/**
 * CSS Grid layout component for two-dimensional layouts.
 * @see https://www.radix-ui.com/themes/docs/components/grid
 */
export const Grid = RadixThemes.Grid

/**
 * Heading component for displaying titles and section headers.
 * @see https://www.radix-ui.com/themes/docs/components/heading
 */
export const Heading = RadixThemes.Heading

/**
 * Hover card component for displaying rich content when hovering over an element.
 * @see https://www.radix-ui.com/themes/docs/components/hover-card
 */
export const HoverCard = RadixThemes.HoverCard

/**
 * Icon button component for compact interactive elements.
 * @see https://www.radix-ui.com/themes/docs/components/icon-button
 */
export const IconButton = RadixThemes.IconButton

/**
 * Inset component for creating bordered content areas.
 * @see https://www.radix-ui.com/themes/docs/components/inset
 */
export const Inset = RadixThemes.Inset

/**
 * Link component for navigation and external references.
 * @see https://www.radix-ui.com/themes/docs/components/link
 */
export const Link = RadixThemes.Link

/**
 * Progress indicator component for showing completion status.
 * @see https://www.radix-ui.com/themes/docs/components/progress
 */
export const Progress = RadixThemes.Progress

/**
 * Radio group component for mutually exclusive selections.
 * @see https://www.radix-ui.com/themes/docs/components/radio-group
 */
export const RadioGroup = RadixThemes.RadioGroup

/**
 * Scroll area component for custom scrollable content regions.
 * @see https://www.radix-ui.com/themes/docs/components/scroll-area
 */
export const ScrollArea = RadixThemes.ScrollArea

/**
 * Section component for semantic content grouping.
 * @see https://www.radix-ui.com/themes/docs/components/section
 */
export const Section = RadixThemes.Section

/**
 * Segmented control component for grouped toggle options.
 * @see https://www.radix-ui.com/themes/docs/components/segmented-control
 */
export const SegmentedControl = RadixThemes.SegmentedControl

/**
 * Select dropdown component for choosing from a list of options.
 * @see https://www.radix-ui.com/themes/docs/components/select
 */
export const Select = RadixThemes.Select

/**
 * Separator component for visual content division.
 * @see https://www.radix-ui.com/themes/docs/components/separator
 */
export const Separator = RadixThemes.Separator

/**
 * Skeleton component for loading placeholder states.
 * @see https://www.radix-ui.com/themes/docs/components/skeleton
 */
export const Skeleton = RadixThemes.Skeleton

/**
 * Spinner component for loading states and progress indication.
 * @see https://www.radix-ui.com/themes/docs/components/spinner
 */
export const Spinner = RadixThemes.Spinner

/**
 * Strong text component for emphasized content.
 * @see https://www.radix-ui.com/themes/docs/components/strong
 */
export const Strong = RadixThemes.Strong

/**
 * Switch component for binary on/off controls.
 * @see https://www.radix-ui.com/themes/docs/components/switch
 */
export const Switch = RadixThemes.Switch

/**
 * Table component for displaying structured data in rows and columns.
 * @see https://www.radix-ui.com/themes/docs/components/table
 */
export const Table = RadixThemes.Table

/**
 * Tabs component for organizing content into multiple panels.
 * @see https://www.radix-ui.com/themes/docs/components/tabs
 */
export const Tabs = RadixThemes.Tabs

/**
 * Text component for displaying content with typography controls.
 * @see https://www.radix-ui.com/themes/docs/components/text
 */
export const Text = RadixThemes.Text

/**
 * Text input field component with various input types and styling.
 * @see https://www.radix-ui.com/themes/docs/components/text-field
 */
export const TextField = RadixThemes.TextField

/**
 * Theme provider component for consistent styling across the application.
 * @see https://www.radix-ui.com/themes/docs/components/theme
 */
export const Theme = RadixThemes.Theme

/**
 * Tooltip component for displaying contextual information on hover.
 * @see https://www.radix-ui.com/themes/docs/components/tooltip
 */
export const Tooltip = RadixThemes.Tooltip

/**
 * Popover component for displaying floating content relative to a trigger.
 * @see https://www.radix-ui.com/themes/docs/components/popover
 */
export const Popover = RadixThemes.Popover

/**
 * Slider component for selecting values from a continuous range.
 * @see https://www.radix-ui.com/themes/docs/components/slider
 */
export const Slider = RadixThemes.Slider
